import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Text,
  VStack,
  Container,
  Heading,
  Image,
  useBreakpointValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "@videojs/http-streaming";
import { TEXTS } from "../constants/texts";
import TopA from "../images/TopA.jpg";
import TopB from "../images/TopB.jpg";
import HaikeiA from "../images/Background/HaikeiA.jpg";
import RoyalAlbertHall from "../images/Assets/RoyalAlbertHall.jpg";
import PRmovieThumbnail2 from "../images/Thumbnail/PRmovieThumbnail2.jpg";

interface CommonImageProps {
  onContextMenu: (e: React.MouseEvent) => void;
  draggable: boolean;
}

const colors = {
  gold: "rgb(233, 203, 141)",
};

const preventDefault = (e: React.MouseEvent): void => {
  e.preventDefault();
};

const commonImageProps: CommonImageProps = {
  onContextMenu: preventDefault,
  draggable: false,
};

const Trailer: React.FC = () => {
  // スクロール方向を検出
  const prevScrollY = useRef(0);
  const [scrollDirection, setScrollDirection] = useState<"down" | "up">("down");
  const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTimeRef = useRef(0);

  // HLS動画のURL
  const hlsVideoUrl =
    "https://d2cqeaqvjfsqr.cloudfront.net/videos/hls/topgun_concert_output.m3u8";

  // ビデオ要素の参照
  const videoElementRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<any>(null);

  // useInView設定の最適化
  const useInViewConfig = useMemo(
    () => ({
      triggerOnce: false,
      threshold: 0.15,
      rootMargin: "10% 0px",
    }),
    []
  );

  // RoyalAlbertHall用のInView設定（より遠い距離から検出）
  const useInViewRoyalAlbertConfig = useMemo(
    () => ({
      triggerOnce: false,
      threshold: 0.15, // 閾値を下げて早めに検出
      rootMargin: "10% 0px", // 上下に余裕を持たせる
    }),
    []
  );

  // タイトルテキスト用のInView設定（実際に画面に表示されてから検出）
  const useInViewTextConfig = useMemo(
    () => ({
      triggerOnce: false,
      threshold: 0.15, // 閾値を下げて早めに検出
      rootMargin: "10% 0px", // 上下に余裕を持たせる
    }),
    []
  );

  // コンポーネント全体がビューポートに入ったかどうかを検出
  const { ref: componentRef } = useInView({
    triggerOnce: false,
    threshold: 0.05,
    rootMargin: "0px 0px 150px 0px",
  });

  // useInViewフック
  const { ref: videoRef, inView: videoInView } = useInView(useInViewConfig);
  const { ref: textRef, inView: textInView } = useInView(useInViewTextConfig);
  const { ref: royalAlbertRef, inView: royalAlbertInView } = useInView(
    useInViewRoyalAlbertConfig
  );
  const { ref: descriptionRef, inView: descriptionInView } =
    useInView(useInViewConfig);

  // アニメーション状態
  const [videoAnimationState, setVideoAnimationState] = useState("hidden");
  const [textAnimationState, setTextAnimationState] = useState("hidden");
  const [royalAlbertAnimationState, setRoyalAlbertAnimationState] =
    useState("hidden");
  const [descriptionAnimationState, setDescriptionAnimationState] =
    useState("hidden");

  // スクロール方向検出とアニメーション状態の更新を統合
  useEffect(() => {
    // 初期スクロール位置を設定
    prevScrollY.current = window.scrollY;

    // モバイルデバイス検出
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const throttleTime = isMobile ? 16 : 50; // モバイルではより高速に反応（約60fps）

    const handleScroll = () => {
      // スロットリング実装
      const now = Date.now();
      if (now - lastScrollTimeRef.current < throttleTime) {
        // 前回のスクロール処理からthrottleTime経っていない場合はスキップ
        if (scrollTimerRef.current === null) {
          // 次回の処理をスケジュール
          scrollTimerRef.current = setTimeout(() => {
            lastScrollTimeRef.current = Date.now();
            scrollTimerRef.current = null;
            handleScroll();
          }, throttleTime);
        }
        return;
      }

      lastScrollTimeRef.current = now;
      const currentScrollY = window.scrollY;
      const previousY = prevScrollY.current;
      const diff = currentScrollY - previousY;
      const isScrollingDown = diff > 0;

      // スクロール方向を更新（小さな変動は無視）
      if (Math.abs(diff) > 2) {
        const newDirection = isScrollingDown ? "down" : "up";

        // 方向が変わった場合、または下スクロールの場合は常に状態を更新
        if (newDirection !== scrollDirection || newDirection === "down") {
          setScrollDirection(newDirection);
        }
      }

      prevScrollY.current = currentScrollY;
    };

    // スクロールイベントをより頻繁に検出
    window.addEventListener("scroll", handleScroll, { passive: true });

    // タッチイベントも監視（モバイル向け）
    if (isMobile) {
      let touchStartY = 0;

      const handleTouchStart = (e: TouchEvent) => {
        touchStartY = e.touches[0].clientY;
      };

      const handleTouchMove = (e: TouchEvent) => {
        const touchY = e.touches[0].clientY;
        const diff = touchStartY - touchY;

        // 明確なスワイプを検出した場合のみ方向を更新
        if (Math.abs(diff) > 10) {
          setScrollDirection(diff > 0 ? "down" : "up");
          touchStartY = touchY;
        }
      };

      window.addEventListener("touchstart", handleTouchStart, {
        passive: true,
      });
      window.addEventListener("touchmove", handleTouchMove, { passive: true });

      return () => {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("touchstart", handleTouchStart);
        window.removeEventListener("touchmove", handleTouchMove);
        if (scrollTimerRef.current) {
          clearTimeout(scrollTimerRef.current);
        }
      };
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
    };
  }, [scrollDirection]); // scrollDirectionの依存性を追加

  // ビデオアニメーション状態の更新 - 先行してアニメーション状態を設定
  useEffect(() => {
    if (videoInView) {
      // 即時実行のrequestAnimationFrameでレンダリングを最適化
      requestAnimationFrame(() => {
        if (scrollDirection === "up") {
          setVideoAnimationState("instant");
        } else {
          // 下スクロール時は常にvisibleにリセットしてからアニメーション
          setVideoAnimationState("hidden");
          requestAnimationFrame(() => {
            setVideoAnimationState("visible");
          });
        }
      });
    } else if (scrollDirection === "up") {
      // ビューポートから外れたときの処理（上スクロール時のみ）
      setVideoAnimationState("hidden");
    }
  }, [videoInView, scrollDirection]);

  // テキストアニメーション状態の更新
  useEffect(() => {
    if (textInView) {
      // 即時実行のrequestAnimationFrameでレンダリングを最適化
      requestAnimationFrame(() => {
        if (scrollDirection === "up") {
          setTextAnimationState("instant");
        } else {
          // 下スクロール時は常にvisibleにリセットしてからアニメーション
          setTextAnimationState("hidden");
          requestAnimationFrame(() => {
            setTextAnimationState("visible");
          });
        }
      });
    } else if (scrollDirection === "up") {
      // ビューポートから外れたときの処理（上スクロール時のみ）
      setTextAnimationState("hidden");
    }
  }, [textInView, scrollDirection]);

  // ロイヤルアルバートホールアニメーション状態の更新
  useEffect(() => {
    if (royalAlbertInView) {
      // 即時実行のrequestAnimationFrameでレンダリングを最適化
      requestAnimationFrame(() => {
        if (scrollDirection === "up") {
          setRoyalAlbertAnimationState("instant");
        } else {
          // 下スクロール時は常にvisibleにリセットしてからアニメーション
          setRoyalAlbertAnimationState("hidden");
          requestAnimationFrame(() => {
            setRoyalAlbertAnimationState("visible");
          });
        }
      });
    } else if (scrollDirection === "up") {
      // ビューポートから外れたときの処理（上スクロール時のみ）
      setRoyalAlbertAnimationState("hidden");
    }
  }, [royalAlbertInView, scrollDirection]);

  // 説明セクションアニメーション状態の更新
  useEffect(() => {
    if (descriptionInView) {
      // 即時実行のrequestAnimationFrameでレンダリングを最適化
      requestAnimationFrame(() => {
        if (scrollDirection === "up") {
          setDescriptionAnimationState("instant");
        } else {
          // 下スクロール時は常にvisibleにリセットしてからアニメーション
          setDescriptionAnimationState("hidden");
          requestAnimationFrame(() => {
            setDescriptionAnimationState("visible");
          });
        }
      });
    } else if (scrollDirection === "up") {
      // ビューポートから外れたときの処理（上スクロール時のみ）
      setDescriptionAnimationState("hidden");
    }
  }, [descriptionInView, scrollDirection]);

  // 画像選択（レスポンシブ）
  const topImage = useBreakpointValue({
    base: TopB,
    md: TopA,
  });

  // アニメーションの方向をレスポンシブに設定
  const animationDirection = useBreakpointValue({
    base: { x: 0, y: 30 },
    md: { x: 0, y: 0 },
  });

  // スライドインアニメーションのバリエーション
  const slideInVariants = {
    hidden: {
      opacity: 0,
      ...animationDirection,
    },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration: 1.2,
        ease: "easeOut",
        delay: 0,
      },
    },
    // 上方向スクロール用の即時表示
    instant: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration: 0,
        ease: "easeOut",
      },
    },
  };

  // HLSビデオのアニメーション設定
  const videoFadeVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        delay: 0.2, // 遅延を短くして要素が見えやすくする
      },
    },
    instant: {
      opacity: 1,
      transition: {
        duration: 0, // 即時表示
        ease: "easeOut",
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.0,
        ease: "easeOut",
        delay: 0.1,
      },
    },
    instant: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0,
        ease: "easeOut",
      },
    },
  };

  // ビデオプレーヤーの初期化
  useEffect(() => {
    // すでにプレーヤーが存在する場合は早期リターン
    if (playerRef.current) return;

    // タイマーを使用して少し遅延させる
    const timer = setTimeout(() => {
      const videoElement = document.getElementById(
        "trailer-video"
      ) as HTMLVideoElement;
      if (!videoElement) {
        return;
      }

      try {
        // Video.jsプレーヤーを初期化
        const player = videojs(
          videoElement,
          {
            autoplay: false,
            controls: true,
            responsive: true,
            fluid: true,
            liveui: false,
            volume: 0.5, // 初期音量を50%に設定
            controlBar: {
              children: [
                "playToggle", // 再生/一時停止ボタン
                "volumePanel", // 音量コントロール
                "progressControl", // シークバー
                "currentTimeDisplay", // 現在の時間
                "timeDivider", // 時間の区切り
                "durationDisplay", // 総時間
                "fullscreenToggle", // フルスクリーンボタン
              ],
              // 不要なボタンを明示的に無効化
              chaptersButton: false,
              descriptionsButton: false,
              subsCapsButton: false,
              audioTrackButton: false,
              playbackRateMenuButton: false,
            },
            // フルスクリーン設定
            fullscreen: {
              alwaysShowControls: true, // フルスクリーン時に常にコントロール表示
              navigationUI: "show", // ブラウザのナビゲーションUIを表示
            },
            html5: {
              hls: {
                enableLowInitialPlaylist: true,
                smoothQualityChange: true,
                overrideNative: true,
                withCredentials: false, // CORSのクレデンシャルを送信しない
                handleManifestRedirects: true, // リダイレクトを処理
              },
              nativeVideoTracks: false,
              nativeAudioTracks: false,
              nativeTextTracks: false,
            },
            sources: [
              {
                src: hlsVideoUrl,
                type: "application/x-mpegURL",
              },
            ],
          },
          function onPlayerReady() {
            // プレーヤー準備完了

            // 明示的に音量を設定
            player.volume(0.5);

            // 音量スライダーのUI表示も50%に設定 - より強力なアプローチ
            try {
              // 直接UIコンポーネントと要素を取得
              const playerAny = player as any;
              const volumeControl = playerAny.controlBar.volumePanel;

              // すべての音量関連コンポーネントを取得し操作するヘルパー関数
              const updateVolumeUI = () => {
                // 内部のVolumeBarコンポーネントで値を設定
                if (volumeControl && volumeControl.volumeBar) {
                  // TypeScriptエラー回避のためのチェックと型アサーション
                  if (typeof volumeControl.volumeBar.value === "function") {
                    volumeControl.volumeBar.value(0.5);
                  }
                  if (typeof volumeControl.volumeBar.update === "function") {
                    volumeControl.volumeBar.update();
                  }
                }

                // DOM要素を直接操作
                const volumeLevel = document.querySelector(".vjs-volume-level");
                if (volumeLevel instanceof HTMLElement) {
                  volumeLevel.style.width = "50%";
                }

                const volumeHandle =
                  document.querySelector(".vjs-volume-handle");
                if (volumeHandle instanceof HTMLElement) {
                  volumeHandle.style.left = "calc(50% - 0.5em)";
                }

                // スライダーUI更新のために再度プレーヤーの音量を設定
                player.volume(0.5);
              };

              // 複数のタイミングでUIを更新
              updateVolumeUI();

              // UIが完全に描画された後で再度更新
              setTimeout(updateVolumeUI, 100);
              setTimeout(updateVolumeUI, 500);

              // ユーザー操作検出のために音量変更イベントを監視
              player.on("volumechange", function () {
                // 最初の数回はUIを強制更新、その後はユーザー操作を尊重
                const v = player.volume();
                console.log("Volume changed:", v);
              });

              // プレーヤーが準備完了したときにも更新
              player.on("ready", updateVolumeUI);

              // メディアが読み込まれたときにも更新
              player.on("loadedmetadata", updateVolumeUI);

              // コントロールが表示されたときにも更新
              player.on("useractive", updateVolumeUI);
            } catch (error) {
              // エラー処理
              console.error("音量UI更新エラー:", error);
            }

            // フルスクリーン時のコントロールバー表示の設定
            player.on("fullscreenchange", function () {
              if (player.isFullscreen()) {
                // TypeScriptエラー回避のため型アサーションを使用
                const playerAny = player as any;
                if (
                  playerAny.controlBar &&
                  typeof playerAny.controlBar.show === "function"
                ) {
                  playerAny.controlBar.show();
                }
              } else {
                // フルスクリーン終了時もコントロールバーを表示したままにする
                const playerAny = player as any;
                if (
                  playerAny.controlBar &&
                  typeof playerAny.controlBar.show === "function"
                ) {
                  playerAny.controlBar.show();
                }
              }
            });

            playerRef.current = player;
          }
        );

        // プレーヤーのソースをセット（明示的に）
        player.src({
          src: hlsVideoUrl,
          type: "application/x-mpegURL",
        });

        // 読み込みを開始
        player.load();

        playerRef.current = player;
      } catch (error) {
        // エラー処理
      }
    }, 500); // 500ms遅延

    // クリーンアップ関数
    return () => {
      clearTimeout(timer);
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [hlsVideoUrl]);

  // HLSファイルの可用性チェック
  useEffect(() => {
    // HLSファイルにアクセスできるかチェック
    const checkHlsAvailability = async () => {
      try {
        const response = await fetch(hlsVideoUrl, { method: "HEAD" });
        if (!response.ok) {
          // エラー処理
        }
      } catch (error) {
        // エラー処理
      }
    };

    checkHlsAvailability();
  }, [hlsVideoUrl]);

  // 左上の余分な要素だけを非表示にするための対策
  useEffect(() => {
    // 枠線を削除し特定の要素を非表示にする関数
    const hideTopLeftElements = () => {
      // フルスクリーン時のコントロール表示用のスタイルを追加
      if (!document.getElementById("vjs-always-show-controls-style")) {
        const styleElement = document.createElement("style");
        styleElement.id = "vjs-always-show-controls-style";
        styleElement.textContent = `
          /* フルスクリーン時にコントロールバーを常に表示 */
          .video-js.vjs-always-show-controls.vjs-fullscreen .vjs-control-bar {
            opacity: 1 !important;
            visibility: visible !important;
            display: flex !important;
            transition: none !important;
          }
          
          /* フルスクリーン時にフェードアウトしない */
          .video-js.vjs-always-show-controls.vjs-fullscreen.vjs-user-inactive .vjs-control-bar {
            opacity: 1 !important;
            visibility: visible !important;
          }
          
          /* 青い線（選択エフェクト）を削除 */
          .video-js .vjs-control:before,
          .video-js .vjs-control:after,
          .video-js .vjs-control-bar button,
          .video-js .vjs-control-bar .vjs-button {
            background: none !important;
            border: none !important;
            box-shadow: none !important;
            outline: none !important;
          }
          
          /* 青い選択エフェクトや境界線を削除 */
          .video-js .vjs-play-control,
          .video-js .vjs-volume-panel,
          .video-js .vjs-time-control,
          .video-js .vjs-fullscreen-control {
            box-shadow: none !important;
            border: none !important;
            background: transparent !important;
          }

          /* プレイヤー全体の青いフォーカス枠を削除 */
          .video-js:focus,
          .video-js *:focus {
            outline: none !important;
          }
    
        `;
        document.head.appendChild(styleElement);
      }
    };

    // 一定間隔で適用（プレーヤーがロードされるのを待つ）
    const interval = setInterval(hideTopLeftElements, 500);

    // 10秒後にインターバルをクリア
    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
      // スタイル要素を削除
      const styleElement = document.getElementById(
        "vjs-always-show-controls-style"
      );
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);

  return (
    <Box pos="relative" overflow="hidden" ref={componentRef}>
      {/* トップ画像 */}
      <Image
        src={topImage}
        alt="top main"
        w="100%"
        objectFit="cover"
        {...commonImageProps}
      />

      {/* メインコンテンツエリア */}
      <Box position="relative">
        {/* 背景画像 - 固定高さを削除して、コンテンツに合わせて自動的に高さを調整 */}
        <Box position="relative">
          <Image
            src={HaikeiA}
            alt="Background"
            w="100%"
            objectFit="cover"
            objectPosition="center bottom"
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            minHeight="100%"
            zIndex="-1"
          />

          <Box
            width="100%"
            px={{
              base: "5%",
              sm: "8%",
              md: "10%",
              lg: "12%",
            }}
            py={{
              base: "40px",
              sm: "50px",
              md: "60px",
              lg: "80px",
            }}
            id="trailer-title" // HLS動画が再度有効になった場合は、trailer-video要素に戻す
          >
            <VStack
              spacing={{
                base: "30px",
                sm: "40px",
                md: "50px",
                lg: "60px",
              }}
              align="center"
            >
              {/* HLS動画 */}

              <motion.div
                ref={videoRef}
                initial="hidden"
                animate={videoAnimationState}
                variants={videoFadeVariants}
                style={{ width: "100%" }}
              >
                <Box
                  w="100%"
                  maxW={{
                    base: "80%",
                    sm: "80%",
                    md: "70%",
                    lg: "70%",
                  }}
                  mx="auto"
                  position="relative"
                  borderRadius={{
                    base: "10px",
                    sm: "15px",
                    md: "20px",
                    lg: "25px",
                  }}
                  overflow="hidden"
                  sx={{
                    "&::before": {
                      content: '""',
                      display: "block",
                      paddingTop: "56.25%", // 16:9のアスペクト比
                    },
                  }}
                >
                  <video
                    id="trailer-video"
                    ref={videoElementRef}
                    className="video-js vjs-big-play-centered vjs-16-9"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      display: "block",
                      backgroundColor: "#000",
                    }}
                    controls
                    preload="auto"
                    playsInline
                    poster={PRmovieThumbnail2}
                    data-setup='{"techOrder": ["html5"]}'
                  ></video>
                </Box>
              </motion.div>

              {/* テキストセクション */}
              <motion.div
                ref={textRef}
                initial="hidden"
                animate={textAnimationState}
                variants={titleVariants}
                style={{ width: "100%" }}
              >
                <VStack
                  spacing={{
                    base: 3,
                    sm: 4,
                    md: 5,
                  }}
                  width="100%"
                  px={{
                    base: 0,
                    sm: 2,
                  }}
                  mx="auto"
                  maxW={{
                    base: "110%",
                    sm: "100%",
                  }}
                >
                  <motion.div
                    key={`title-1`}
                    initial="hidden"
                    animate={textAnimationState}
                    variants={titleVariants}
                  >
                    <Text
                      color="white"
                      fontSize={{
                        base: "14px",
                        sm: "16px",
                        md: "24px",
                        lg: "32px",
                        "2xl": "40px",
                      }}
                      textAlign="center"
                      fontWeight="bold"
                      mb={{
                        base: "-10px",
                        sm: "-15px",
                        md: "-15px",
                        lg: "-15px",
                        "2xl": "-15px",
                      }}
                    >
                      {TEXTS.TOPGUN_TITLE}
                    </Text>
                  </motion.div>

                  <motion.div
                    key={`title-2`}
                    initial="hidden"
                    animate={textAnimationState}
                    variants={titleVariants}
                  >
                    <Text
                      color="white"
                      fontSize={{
                        base: "14px",
                        sm: "16px",
                        md: "24px",
                        lg: "32px",
                        "2xl": "40px",
                      }}
                      textAlign="center"
                      fontWeight="bold"
                      mb={{
                        base: 4,
                        sm: 5,
                        md: 6,
                        lg: 8,
                      }}
                    >
                      {TEXTS.TOPGUN_TITLE_2}
                    </Text>
                  </motion.div>

                  {/* 残りのテキスト */}
                  <VStack spacing={2} width="100%">
                    {Object.values(TEXTS)
                      .slice(2, 14)
                      .map((content: string, i) => (
                        <motion.div
                          key={i}
                          initial="hidden"
                          animate={textAnimationState}
                          variants={{
                            hidden: { opacity: 0, y: 20 },
                            visible: {
                              opacity: 1,
                              y: 0,
                              transition: {
                                duration: 1.0,
                                delay: 0.1 + i * 0.1,
                              },
                            },
                            instant: {
                              opacity: 1,
                              y: 0,
                              transition: {
                                duration: 0,
                              },
                            },
                          }}
                        >
                          <Text
                            color="white"
                            fontSize={{
                              base: "11px",
                              sm: "11px",
                              md: "15px",
                              lg: "16px",
                              xl: "20px",
                              "2xl": "22px",
                            }}
                            textAlign="center"
                            lineHeight={{
                              base: "1.3",
                              sm: "1.3",
                              md: "1.3",
                              lg: "1.3",
                              xl: "1.3",
                              "2xl": "1.3",
                            }}
                            width="100%"
                            wordBreak="keep-all"
                            overflowWrap="break-word"
                            whiteSpace={{
                              base: "pre-line",
                              xl: "normal",
                            }}
                            letterSpacing={{
                              base: "-0.01em",
                              sm: "normal",
                            }}
                            px={{
                              base: "1",
                              sm: "0",
                            }}
                          >
                            {content}
                          </Text>
                        </motion.div>
                      ))}
                  </VStack>
                </VStack>
              </motion.div>

              {/* ロイヤルアルバートホール画像 */}
              <Box width="100%" textAlign="center">
                <Box
                  width={{
                    base: "75%",
                    sm: "75%",
                    md: "70%",
                    lg: "70%",
                  }}
                  height="auto"
                  overflow="hidden"
                  mx="auto"
                  display="block"
                  ref={royalAlbertRef}
                >
                  <motion.div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                      transformOrigin: "center",
                    }}
                    initial="hidden"
                    animate={royalAlbertAnimationState}
                    variants={{
                      hidden: {
                        opacity: 0,
                        scale: 1.2,
                      },
                      visible: {
                        opacity: 1,
                        scale: 1,
                        transition: {
                          duration: 1.5,
                          ease: "easeOut",
                        },
                      },
                      instant: {
                        opacity: 1,
                        scale: 1,
                        transition: {
                          duration: 0,
                        },
                      },
                    }}
                  >
                    <Image
                      src={RoyalAlbertHall}
                      alt="RoyalAlbertHall"
                      width="100%"
                      height="auto"
                      {...commonImageProps}
                    />
                  </motion.div>
                </Box>
              </Box>

              {/* 説明セクション */}
              <motion.div
                ref={descriptionRef}
                initial="hidden"
                animate={descriptionAnimationState}
                variants={slideInVariants}
                style={{ width: "100%" }}
              >
                <Container
                  maxW={{
                    base: "100%",
                    sm: "100%",
                    md: "100%",
                    lg: "100%",
                    xl: "93%",
                    "2xl": "80%",
                  }}
                  mb={{
                    base: "20px",
                    sm: "30px",
                    md: "40px",
                    lg: "50px",
                  }}
                  px={{
                    base: "2",
                    sm: undefined,
                    md: undefined,
                  }}
                >
                  <Box
                    borderColor={colors.gold}
                    borderLeft={`2px solid ${colors.gold}`}
                    borderRight={`2px solid ${colors.gold}`}
                    borderBottom={`2px solid ${colors.gold}`}
                    p={{
                      base: 3,
                      sm: 5,
                      md: 6,
                      lg: 8,
                    }}
                    pos="relative"
                    pt={{
                      base: "30px",
                      sm: "35px",
                      md: "40px",
                      lg: "50px",
                    }}
                    sx={{
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: {
                          base: 0,
                          sm: 0,
                          md: 0,
                          lg: 0,
                        },
                        width: {
                          base: "30%",
                          sm: "30%",
                          md: "30%",
                          lg: "30%",
                        },
                        height: "2px",
                        background: colors.gold,
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        right: {
                          base: 0,
                          sm: 0,
                          md: 0,
                          lg: 0,
                        },
                        width: {
                          base: "30%",
                          sm: "30%",
                          md: "30%",
                          lg: "30%",
                        },
                        height: "2px",
                        background: colors.gold,
                      },
                    }}
                  >
                    <Heading
                      pos="absolute"
                      top="0"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      px={{
                        base: 2,
                        sm: 3,
                        md: 4,
                      }}
                      py={{
                        base: 1,
                        sm: 1.5,
                        md: 2,
                      }}
                      color="white"
                      fontSize={{
                        base: "13px",
                        sm: "15px",
                        md: "20px",
                        lg: "30px",
                        xl: "32px",
                        "2xl": "36px",
                      }}
                      whiteSpace={{
                        base: "normal",
                        md: "nowrap",
                      }}
                      textAlign="center"
                      minWidth="fit-content"
                    >
                      <motion.span
                        initial="hidden"
                        animate={descriptionAnimationState}
                        variants={titleVariants}
                        style={{ display: "inline-block" }}
                      >
                        {TEXTS.Description.title}
                      </motion.span>
                      <Text
                        as="span"
                        fontSize={{
                          base: "8px",
                          sm: "8px",
                          md: "8px",
                          lg: "21px",
                          xl: "20px",
                          "2xl": "20px",
                        }}
                        position="relative"
                        top={{
                          base: "-5px",
                          sm: "-6px",
                          md: "-8px",
                          lg: "-10px",
                          "2xl": "-12px",
                        }}
                      >
                        <motion.span
                          initial="hidden"
                          animate={descriptionAnimationState}
                          variants={titleVariants}
                          style={{ display: "inline-block" }}
                        >
                          {TEXTS.Description.title_2}
                        </motion.span>
                      </Text>
                      <motion.span
                        initial="hidden"
                        animate={descriptionAnimationState}
                        variants={titleVariants}
                        style={{ display: "inline-block" }}
                      >
                        {TEXTS.Description.title_3}
                      </motion.span>
                    </Heading>
                    <VStack spacing={0}>
                      {TEXTS.Description.content.map((text, index) => (
                        <motion.div
                          key={index}
                          initial="hidden"
                          animate={descriptionAnimationState}
                          variants={{
                            hidden: { opacity: 0, y: 20 },
                            visible: {
                              opacity: 1,
                              y: 0,
                              transition: {
                                duration: 1.0,
                                delay: 0.1 + index * 0.1,
                              },
                            },
                            instant: {
                              opacity: 1,
                              y: 0,
                              transition: {
                                duration: 0,
                              },
                            },
                          }}
                          style={{ width: "100%" }}
                        >
                          <Text
                            key={index}
                            color="white"
                            fontSize={{
                              base: index === 2 ? "8px" : "11px",
                              sm: index === 2 ? "10px" : "11px",
                              md: index === 2 ? "12px" : "15px",
                              lg: index === 2 ? "14px" : "18px",
                              xl: index === 2 ? "16px" : "20px",
                              "2xl": index === 2 ? "16px" : "22px",
                            }}
                            textAlign="center"
                            mt={
                              index === 2
                                ? {
                                    base: 3,
                                    sm: 4,
                                    md: 5,
                                    lg: 6,
                                  }
                                : 0
                            }
                            whiteSpace={{
                              base: "normal",
                              md: "pre-line",
                            }}
                            wordBreak="keep-all"
                            overflowWrap="break-word"
                            lineHeight={{
                              base: "1.5",
                              sm: undefined,
                              md: undefined,
                            }}
                            letterSpacing={{
                              base: "-0.01em",
                              sm: undefined,
                            }}
                          >
                            {text}
                          </Text>
                        </motion.div>
                      ))}
                    </VStack>
                  </Box>
                </Container>
              </motion.div>
            </VStack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Trailer;
